;;
;; This program is written in a literate style. That means; this
;; source-file is generated and you should not make direct edits. You
;; should instead make your contributions in `index.org` and tangle it.
;; Please have a look at https://orgmode.org/ for more information.
;;
;; --------------------------------------------------------------------
;;
;;   MIT License
;;   
;;   Copyright (C) 2024 DNV GL
;;   
;;   Permission is hereby granted, free of charge, to any person obtaining a copy
;;   of this software and associated documentation files (the "Software"), to deal
;;   in the Software without restriction, including without limitation the rights
;;   to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
;;   copies of the Software, and to permit persons to whom the Software is
;;   furnished to do so, subject to the following conditions:
;;   
;;   The above copyright notice and this permission notice shall be included in all
;;   copies or substantial portions of the Software.
;;   
;;   THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
;;   IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
;;   FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
;;   AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
;;   LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
;;   OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
;;   SOFTWARE.
;;
(ns reagent-flow.core
  "A ClojureScript library that wraps ReactFlow"
  (:require
   [camel-snake-kebab.core :refer [->kebab-case ->camelCase]]
   [clojure.set :refer [rename-keys]]
   [clojure.string :as str]
   [clojure.walk :refer [postwalk]]
   [cljs.core :refer [IDeref IEditableCollection]]
   [medley.core :refer [map-keys map-vals]]
   [reagent.core :as r]
   ["@xyflow/react$default" :as ReactFlow]
   ["@xyflow/react" :as rf
    :refer [addEdge
            applyEdgeChanges
            applyNodeChanges
            Background
            BaseEdge
            BezierEdge
            ControlButton
            Controls
            EdgeLabelRenderer
            EdgeText
            Handle
            MiniMap
            NodeResizer
            NodeResizeControl
            NodeToolbar
            Panel
            Position
            ReactFlow
            ReactFlowProvider
            SimpleBezierEdge
            SmoothStepEdge
            StepEdge
            StraightEdge
            boxToRect
            getBezierPath 
            getBoundsOfRects
            getConnectedEdes
            getIncomers
            getMarkerEnd 
            getOutgoers
            getRectOfNodes
            getSimpleBezierPath 
            getSmoothStepPath 
            getStraightPath 
            getTransformForBounds
            internalsSymbol
            isEdge
            isNode
            rectToBox
            updateEdge
            useReactFlow
            useUpdateNodeInternals
            useNodes
            useEdges
            useViewport
            useKeyPress
            useStore
            useStoreApi
            useOnViewportChange
            useOnSelectionChange
            useNodesInitialized
            useNodesState 
            useEdgesState 
            ]]))

(def background (r/adapt-react-class Background))
(def base-edge (r/adapt-react-class BaseEdge))
(def bezier-edge (r/adapt-react-class BezierEdge))
(def control-button (r/adapt-react-class ControlButton))
(def controls (r/adapt-react-class Controls))
(def edge-label-renderer (r/adapt-react-class EdgeLabelRenderer))
(def edge-text (r/adapt-react-class EdgeText))
(def handle (r/adapt-react-class Handle))
(def mini-map (r/adapt-react-class MiniMap))
(def node-resizer (r/adapt-react-class NodeResizer))
(def node-resize-control (r/adapt-react-class NodeResizeControl))
(def node-toolbar (r/adapt-react-class NodeToolbar))
(def panel (r/adapt-react-class Panel))
(def position (r/adapt-react-class Position))
(def ^:private react-flow (r/adapt-react-class ReactFlow))
(def ^:private reagent-flow-provider (r/adapt-react-class ReactFlowProvider))
(def simple-bezier-edge (r/adapt-react-class SimpleBezierEdge))
(def smooth-step-edge (r/adapt-react-class SmoothStepEdge))
(def step-edge (r/adapt-react-class StepEdge))
(def straight-edge (r/adapt-react-class StraightEdge))

(def ^{:private true} box-to-rect boxToRect)
(def ^{:private true} get-bezier-path getBezierPath)
(def ^{:private true} get-bounds-of-rects getBoundsOfRects)
(def ^{:private true} get-connected-edes getConnectedEdes)
(def ^{:private true} get-incomers getIncomers)
(def ^{:private true} get-marker-end getMarkerEnd)
(def ^{:private true} get-outgoers getOutgoers)
(def ^{:private true} get-rect-of-nodes getRectOfNodes)
(def ^{:private true} get-simple-bezier-path getSimpleBezierPath)
(def ^{:private true} get-smooth-step-path getSmoothStepPath)
(def ^{:private true} get-straight-path getStraightPath)
(def ^{:private true} get-transform-for-bounds getTransformForBounds)
(def ^{:private true} internals-symbol internalsSymbol)
(def ^{:private true} is-edge isEdge)
(def ^{:private true} is-node isNode)
(def ^{:private true} rect-to-box rectToBox)
(def ^{:private true} update-edge updateEdge)
(def ^{:private true} use-edges useEdges)
(def ^{:private true} use-edges-state useEdgesState)
(def ^{:private true} use-key-press useKeyPress)
(def ^{:private true} use-nodes useNodes)
(def ^{:private true} use-nodes-initialized useNodesInitialized)
(def ^{:private true} use-nodes-state useNodesState)
(def ^{:private true} use-on-selection-change useOnSelectionChange)
(def ^{:private true} use-on-viewport-change useOnViewportChange)
(def ^{:private true} use-reagent-flow useReactFlow)
(def ^{:private true} use-store useStore)
(def ^{:private true} use-store-api useStoreApi)
(def ^{:private false} use-update-node-internals useUpdateNodeInternals)
(def ^{:private true} use-viewport useViewport)
(def ^{:nodoc true :const true} FlowPosition rf/Position)
(def ^{:nodoc true :const true} FlowXYPosition rf/XYPosition)
(def ^{:nodoc true :const true} FlowXYZPosition rf/XYZPosition)
(def ^{:nodoc true :const true} FlowDimensions rf/Dimensions)
(def ^{:nodoc true :const true} FlowRect rf/Rect)
(def ^{:nodoc true :const true} FlowBox rf/Box)
(def ^{:nodoc true :const true} FlowTransform rf/Transform)
(def ^{:nodoc true :const true} FlowCoordinateExtent rf/CoordinateExtent)
(def ^{:nodoc true :const true} FlowNode rf/Node)
(def ^{:nodoc true :const true} FlowNodeMouseHandler rf/NodeMouseHandler)
(def ^{:nodoc true :const true} FlowNodeDragHandler rf/NodeDragHandler)
(def ^{:nodoc true :const true} FlowSelectionDragHandler rf/SelectionDragHandler)
(def ^{:nodoc true :const true} FlowWrapNodeProps rf/WrapNodeProps)
(def ^{:nodoc true :const true} FlowNodeProps rf/NodeProps)
(def ^{:nodoc true :const true} FlowNodeHandleBounds rf/NodeHandleBounds)
(def ^{:nodoc true :const true} FlowNodeDimensionUpdate rf/NodeDimensionUpdate)
(def ^{:nodoc true :const true} FlowNodeInternals rf/NodeInternals)
(def ^{:nodoc true :const true} FlowNodeBounds rf/NodeBounds)
(def ^{:nodoc true :const true} FlowNodeDragItem rf/NodeDragItem)
(def ^{:nodoc true :const true} FlowNodeOrigin rf/NodeOrigin)
(def ^{:nodoc true :const true} FlowReactFlowJsonObject rf/ReactFlowJsonObject)
(def ^{:nodoc true :const true} FlowInstance rf/Instance)
(def ^{:nodoc true :const true} FlowReactFlowInstance rf/ReactFlowInstance)
(def ^{:nodoc true :const true} FlowHandleType rf/HandleType)
(def ^{:nodoc true :const true} FlowStartHandle rf/StartHandle)
(def ^{:nodoc true :const true} FlowHandleProps rf/HandleProps)
(def ^{:nodoc true :const true} FlowNodeTypes rf/NodeTypes)
(def ^{:nodoc true :const true} FlowNodeTypesWrapped rf/NodeTypesWrapped)
(def ^{:nodoc true :const true} FlowEdgeTypes rf/EdgeTypes)
(def ^{:nodoc true :const true} FlowEdgeTypesWrapped rf/EdgeTypesWrapped)
(def ^{:nodoc true :const true} FlowFitView rf/FitView)
(def ^{:nodoc true :const true} FlowProject rf/Project)
(def ^{:nodoc true :const true} FlowOnNodesChange rf/OnNodesChange)
(def ^{:nodoc true :const true} FlowOnEdgesChange rf/OnEdgesChange)
(def ^{:nodoc true :const true} FlowOnNodesDelete rf/OnNodesDelete)
(def ^{:nodoc true :const true} FlowOnEdgesDelete rf/OnEdgesDelete)
(def ^{:nodoc true :const true} FlowOnMove rf/OnMove)
(def ^{:nodoc true :const true} FlowOnMoveStart rf/OnMoveStart)
(def ^{:nodoc true :const true} FlowOnMoveEnd rf/OnMoveEnd)
(def ^{:nodoc true :const true} FlowZoomInOut rf/ZoomInOut)
(def ^{:nodoc true :const true} FlowZoomTo rf/ZoomTo)
(def ^{:nodoc true :const true} FlowGetZoom rf/GetZoom)
(def ^{:nodoc true :const true} FlowGetViewport rf/GetViewport)
(def ^{:nodoc true :const true} FlowSetViewport rf/SetViewport)
(def ^{:nodoc true :const true} FlowSetCenter rf/SetCenter)
(def ^{:nodoc true :const true} FlowFitBounds rf/FitBounds)
(def ^{:nodoc true :const true} FlowOnInit rf/OnInit)
(def ^{:nodoc true :const true} FlowConnection rf/Connection)
(def ^{:nodoc true :const true} FlowConnectionMode rf/ConnectionMode)
(def ^{:nodoc true :const true} FlowOnConnect rf/OnConnect)
(def ^{:nodoc true :const true} FlowFitViewOptions rf/FitViewOptions)
(def ^{:nodoc true :const true} FlowOnConnectStartParams rf/OnConnectStartParams)
(def ^{:nodoc true :const true} FlowOnConnectStart rf/OnConnectStart)
(def ^{:nodoc true :const true} FlowOnConnectEnd rf/OnConnectEnd)
(def ^{:nodoc true :const true} FlowViewport rf/Viewport)
(def ^{:nodoc true :const true} FlowKeyCode rf/KeyCode)
(def ^{:nodoc true :const true} FlowSnapGrid rf/SnapGrid)
(def ^{:nodoc true :const true} FlowPanOnScrollMode rf/PanOnScrollMode)
(def ^{:nodoc true :const true} FlowViewportHelperFunctionOptions rf/ViewportHelperFunctionOptions)
(def ^{:nodoc true :const true} FlowSetCenterOptions rf/SetCenterOptions)
(def ^{:nodoc true :const true} FlowFitBoundsOptions rf/FitBoundsOptions)
(def ^{:nodoc true :const true} FlowUnselectNodesAndEdgesParams rf/UnselectNodesAndEdgesParams)
(def ^{:nodoc true :const true} FlowOnViewportChange rf/OnViewportChange)
(def ^{:nodoc true :const true} FlowViewportHelperFunctions rf/ViewportHelperFunctions)
(def ^{:nodoc true :const true} FlowReactFlowStore rf/ReactFlowStore)
(def ^{:nodoc true :const true} FlowReactFlowActions rf/ReactFlowActions)
(def ^{:nodoc true :const true} FlowReactFlowState rf/ReactFlowState)
(def ^{:nodoc true :const true} FlowUpdateNodeInternals rf/UpdateNodeInternals)
(def ^{:nodoc true :const true} FlowOnSelectionChangeParams rf/OnSelectionChangeParams)
(def ^{:nodoc true :const true} FlowOnSelectionChangeFunc rf/OnSelectionChangeFunc)
(def ^{:nodoc true :const true} FlowPanelPosition rf/PanelPosition)
(def ^{:nodoc true :const true} FlowProOptions rf/ProOptions)
(def ^{:nodoc true :const true} FlowSmoothStepPathOptions rf/SmoothStepPathOptions)
(def ^{:nodoc true :const true} FlowBezierPathOptions rf/BezierPathOptions)
(def ^{:nodoc true :const true} FlowEdge rf/Edge)
(def ^{:nodoc true :const true} FlowDefaultEdgeOptions rf/DefaultEdgeOptions)
(def ^{:nodoc true :const true} FlowEdgeMouseHandler rf/EdgeMouseHandler)
(def ^{:nodoc true :const true} FlowWrapEdgeProps rf/WrapEdgeProps)
(def ^{:nodoc true :const true} FlowEdgeProps rf/EdgeProps)
(def ^{:nodoc true :const true} FlowBaseEdgeProps rf/BaseEdgeProps)
(def ^{:nodoc true :const true} FlowSmoothStepEdgeProps rf/SmoothStepEdgeProps)
(def ^{:nodoc true :const true} FlowBezierEdgeProps rf/BezierEdgeProps)
(def ^{:nodoc true :const true} FlowEdgeTextProps rf/EdgeTextProps)
(def ^{:nodoc true :const true} FlowConnectionLineType rf/ConnectionLineType)
(def ^{:nodoc true :const true} FlowConnectionLineComponentProps rf/ConnectionLineComponentProps)
(def ^{:nodoc true :const true} FlowConnectionLineComponent rf/ConnectionLineComponent)
(def ^{:nodoc true :const true} FlowOnEdgeUpdateFunc rf/OnEdgeUpdateFunc)
(def ^{:nodoc true :const true} FlowEdgeMarker rf/EdgeMarker)
(def ^{:nodoc true :const true} FlowEdgeMarkerType rf/EdgeMarkerType)
(def ^{:nodoc true :const true} FlowMarkerType rf/MarkerType)
(def ^{:nodoc true :const true} FlowReactFlowProps rf/ReactFlowProps)
(def ^{:nodoc true :const true} FlowReactFlowRefType rf/ReactFlowRefType)
(def ^{:nodoc true :const true} FlowNodeDimensionChange rf/NodeDimensionChange)
(def ^{:nodoc true :const true} FlowNodePositionChange rf/NodePositionChange)
(def ^{:nodoc true :const true} FlowNodeSelectionChange rf/NodeSelectionChange)
(def ^{:nodoc true :const true} FlowNodeRemoveChange rf/NodeRemoveChange)
(def ^{:nodoc true :const true} FlowNodeAddChange rf/NodeAddChange)
(def ^{:nodoc true :const true} FlowNodeResetChange rf/NodeResetChange)
(def ^{:nodoc true :const true} FlowNodeChange rf/NodeChange)
(def ^{:nodoc true :const true} FlowEdgeSelectionChange rf/EdgeSelectionChange)
(def ^{:nodoc true :const true} FlowEdgeRemoveChange rf/EdgeRemoveChange)
(def ^{:nodoc true :const true} FlowEdgeAddChange rf/EdgeAddChange)
(def ^{:nodoc true :const true} FlowEdgeResetChange rf/EdgeResetChange)
(def ^{:nodoc true :const true} FlowEdgeChange rf/EdgeChange)

(def -->kebab-case (memoize ->kebab-case))

(def -->camelCase (memoize ->camelCase))

(defn- ->params
  "Normalize arguments to always have the form [props children] like
   hiccup elements."
  [args]
  (cond-> args
    (-> args first map? not) (conj nil)))

(defn- change-keys
  "Walks a map and replaces all keys by applying function to the keys."
  [m f]
  (let [f (fn [[k v]] (if (or (string? k) (keyword? k)) [(f k) v] [k v]))]
    (postwalk (fn [x] (if (map? x) (into {} (map f x)) x)) m)))

(defn- flowjs->clj [o]
  "Convert a JavaScript object to a Clojure map with kebab-cased keys."
  (let [obj (js->clj o :keywordize-keys true)]
    (if (map? obj)
      (change-keys (dissoc obj "") -->kebab-case)
      (if (vector? obj)
        (map flowjs->clj obj)
        obj))))

(defn- clj->flowjs
  "Convert Clojure map into a JavaScript object with camelCased keys."
  [o]
  (->> (change-keys o -->camelCase)
       (clj->js)))

(defn- apply-changes [f delta src]
  (-> (f (clj->flowjs delta) (clj->flowjs src))
      (flowjs->clj)))

(defn- react-flowify [types]
  (clj->js ((partial map-vals r/reactify-component) types)))

(defn- reagent-flow*
  [[on-viewport-change on-viewport-start on-viewport-end & args]]
  (let [[params & children] (->params args)
        node-types          (when-let [types (:node-types params)] (react-flowify types))
        edge-types          (when-let [types (:edge-types params)] (react-flowify types))
        on-init             (when-let [init (:on-init params)]
                              (fn [provider]
                                (let [provider                     (flowjs->clj provider)
                                      {:keys [set-center screen-to-flow-position]} provider]
                                  (init (assoc provider
                                               :set-center (fn [x y & options] (set-center x y (clj->js (first options))))
                                               :screen-to-flow-position #(screen-to-flow-position (clj->js %)))))))
        on-nodes-change     (when-let [node-change (:on-nodes-change params)]
                              (fn [delta] (node-change (flowjs->clj delta))))
        on-edges-change     (when-let [edge-change (:on-edges-change params)]
                              (fn [delta] (edge-change (flowjs->clj delta))))
        on-connect          (when-let [connect (:on-connect params)]
                              (fn [delta] (connect (flowjs->clj delta))))
        on-connect-start    (when-let [connect-start (:on-connect-start params)]
                              (fn [event params] (connect-start event (flowjs->clj params))))
        params              (dissoc params :node-types :edge-types)]
    (fn [[on-viewport-change on-viewport-start on-viewport-end & args]]
      (let [[params & children] (->params args)
            params              (merge (dissoc params :node-types :edge-types :edges)
                                       (map-vals clj->js params)
                                       {:edges (clj->flowjs (:edges params))}
                                       (when node-types {:node-types node-types})
                                       (when edge-types {:edge-types edge-types})
                                       (when on-init {:on-init on-init})
                                       (when on-nodes-change {:on-nodes-change on-nodes-change})
                                       (when on-edges-change {:on-edges-change on-edges-change})
                                       (when on-connect {:on-connect on-connect})
                                       (when on-connect-start {:on-connect-start on-connect-start}))]
        (when (or (some? on-viewport-change)
                  (some? on-viewport-start)
                  (some? on-viewport-end))
          (use-on-viewport-change
           (clj->js
            (merge {}
                   (when (some? on-viewport-change)
                     {:onChange #(on-viewport-change (flowjs->clj %))})
                   (when (some? on-viewport-start)
                     {:onStart #(on-viewport-start (flowjs->clj %))})
                   (when (some? on-viewport-end)
                     {:onEnd #(on-viewport-end (flowjs->clj %))})))))
        (into [react-flow params] children)))))

(defn apply-node-changes
  "Returns a vector of nodes with `changes` applied to the `source`."
  [changes source]
  (vec (apply-changes applyNodeChanges changes source)))

(defn apply-edge-changes
  "Returns a vector of edges with `changes` applied to the `source`."
  [changes source]
  (vec (apply-changes applyEdgeChanges changes source)))

(defn add-edge
  "Returns a vector of edges with `edge` added to the `source`."
  [edge source]
  (vec (apply-changes addEdge edge source)))

(defn get-node-by-id
  "Returns a map of the node with `id` from `nodes`. 
   The returned map is supplemented with the keyword `index`.

   Returns `nil` if the node is not found."
  [nodes id]
  (when (some? id)
   (letfn [(item-with-id [idx itm]
             (when (= (name id) (name (:id itm)))
               (assoc itm :index idx)))]
     (->> nodes
          (keep-indexed item-with-id)
          (first)))))

(defn get-connections-by-node-id
  "Returns a vector of connections where the node with `id` is either
   the source or the target.

   Returns an empty vector if no connections are found."
  [connections id & which]
  (let [which (or which [:source :target])]
    (when (some? id)
      (letfn [(items-with-id [idx itm]
                (when (some #(= (name id) (name %)) (map #(get itm %) which))
                  (assoc itm :index idx)))]
        (->> connections
             (keep-indexed items-with-id)
             (into []))))))

(defn reagent-flow
  "This is the main component of `reagent-flow`. It differs from
  `ReactFlow` in a few ways.

   - You pass regular Clojure data-structures to all paramaters, so
     vectors instead of arrays, maps instead of objects and so on.
   - Viewport events are baked in, so you use the events
     `on-viewport-(change|start|end)` to listen for changes in the
     Viewport.
   - reagent-flow-provider is also used, so if you need to have
     multiple flows on the same page, just be sure to give each of them a
     unique `id`.

   Note!
   Node-types & edge-types are called directly from within ReactFlow,
   so the parameters returned are in their JavaScript-form. A nice
   pattern, is to only rely on the `id` from the parameters and do
   lookups in your state manually.
   Ex.
   (defn- custom-node [{:keys [id]}]
     (let [node (flow/get-node-by-id @nodes id)
           data (:data node)]
       [:p (:label data)]))"
  [params & children]
  (let [on-viewport-change (:on-viewport-change params)
        on-viewport-start  (:on-viewport-start params)
        on-viewport-end    (:on-viewport-end params)
        params             (dissoc params :on-viewport-change :on-viewport-start :on-viewport-end)]
    [reagent-flow-provider
     [:f> reagent-flow*
      (into [on-viewport-change
             on-viewport-start
             on-viewport-end
             params] children)]]))
